import React, { useState, useEffect } from 'react';
import { MapPin, Mail, Phone, Send, CheckCircle2 } from 'lucide-react';
import { Language } from '../types';
import { getSiteBranding } from '../services/siteSettingsStorage';

interface ContactSectionProps {
  currentLang: Language;
  prefilledService?: string;
}

export const ContactSection: React.FC<ContactSectionProps> = ({ currentLang, prefilledService }) => {
  const branding = getSiteBranding();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: 'Consulting IT',
    message: ''
  });
  
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaVal1, setCaptchaVal1] = useState(0);
  const [captchaVal2, setCaptchaVal2] = useState(0);

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    setCaptchaVal1(Math.floor(Math.random() * 10) + 1);
    setCaptchaVal2(Math.floor(Math.random() * 10) + 1);
    setCaptchaAnswer('');
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (prefilledService) {
      setFormData(prev => ({ ...prev, subject: prefilledService }));
    }
  }, [prefilledService]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Anti-robot check
    if (parseInt(captchaAnswer, 10) !== captchaVal1 + captchaVal2) {
      setErrorMsg(
        currentLang === 'ar' 
          ? 'إجابة التحقق الأمني غير صحيحة. يرجى إعادة المحاولة.' 
          : currentLang === 'en' 
          ? 'Incorrect anti-robot answer. Please try again.' 
          : 'Réponse anti-robot incorrecte. Veuillez réessayer.'
      );
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);
    setSuccessMsg('');
    setErrorMsg('');

    try {
      const response = await fetch('https://formsubmit.co/ajax/contact@cherifhaddad.com', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          Nom: formData.name,
          Email: formData.email,
          Téléphone: formData.phone,
          Entreprise: formData.company,
          Sujet: formData.subject,
          Message: formData.message,
          _subject: `Nouveau message: ${formData.subject}`,
          _replyto: formData.email,
          _template: 'box'
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMsg(
          currentLang === 'ar'
            ? 'تم إرسال رسالتكم بنجاح. سيتواصل معكم فريقنا قريباً.'
            : currentLang === 'en' 
            ? 'Your message has been sent successfully. We will reply shortly.' 
            : 'Votre message a été envoyé avec succès.'
        );
        setFormData({ name: '', email: '', phone: '', company: '', subject: 'Consulting IT', message: '' });
        generateCaptcha();
      } else {
        setErrorMsg(
          currentLang === 'ar'
            ? 'تعذر إرسال الرسالة. يرجى المحاولة مرة أخرى.'
            : currentLang === 'en' 
            ? 'Failed to send message.' 
            : "Échec de l'envoi du message."
        );
        generateCaptcha();
      }
    } catch (error) {
      setErrorMsg(
        currentLang === 'ar'
          ? 'حدث خطأ غير متوقع. يرجى المحاولة لاحقاً.'
          : currentLang === 'en' 
          ? 'An error occurred.' 
          : 'Une erreur est survenue.'
      );
      generateCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 bg-slate-50 dark:bg-slate-950 transition-colors">
      <div className="w-[90%] lg:w-[70%] max-w-none mx-auto px-6">
        
        {/* Header */}
        <div className="mb-12">
          <h2 className="text-3xl font-semibold text-slate-900 dark:text-white mb-4">
            {currentLang === 'ar' ? 'اتصل بنا وطلب استشارة' : currentLang === 'en' ? 'Get in Touch & Request a Quote' : 'Contactez-nous & Devis'}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {currentLang === 'ar'
              ? 'هل تحتاج إلى استشارة في التدقيق، الجودة أو تأهيل النظم؟ فريقنا المتخصص رهن إشارتكم لمرافقتكم في مشاريعكم الأكثر استراتيجية.'
              : currentLang === 'en' 
              ? "We're here to help you transform your business. Reach out to discuss consulting, audits, or ISO support." 
              : "Besoin d'une expertise en audit ou conseil IT ? Notre équipe est à votre disposition pour vous accompagner dans vos projets les plus complexes."}
          </p>
        </div>

        {/* Top Blocks: Call / Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 flex flex-col items-center justify-center text-center rounded-sm">
            <Phone className="w-6 h-6 text-slate-700 dark:text-slate-300 mb-3" />
            <h4 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">
              {currentLang === 'ar' ? 'اتصل بنا هاتفياً' : currentLang === 'en' ? 'Call Us' : 'Appeler'}
            </h4>
            <span className="text-xs text-slate-600 dark:text-slate-400 font-mono">{branding.headerPhone}</span>
          </div>
          
          <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 flex flex-col items-center justify-center text-center rounded-sm">
            <Mail className="w-6 h-6 text-slate-700 dark:text-slate-300 mb-3" />
            <h4 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">
              {currentLang === 'ar' ? 'راسلنا عبر البريد' : currentLang === 'en' ? 'Email Us' : 'Email'}
            </h4>
            <span className="text-xs text-slate-600 dark:text-slate-400 font-mono">{branding.headerEmail}</span>
          </div>
        </div>

        {/* Contact Form */}
        <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 rounded-sm">
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">
            {currentLang === 'ar' ? 'إرسال رسالة مباشرة' : currentLang === 'en' ? 'Send a Message' : 'Envoyer un message'}
          </h3>
          
          {successMsg && (
            <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-sm flex items-center gap-3 text-emerald-700 dark:text-emerald-400">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">{successMsg}</span>
            </div>
          )}
          {errorMsg && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-sm text-red-700 dark:text-red-400 text-sm font-medium">
              {errorMsg}
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                {currentLang === 'ar' ? 'الاسم واللقب' : currentLang === 'en' ? 'Full Name' : 'Nom complet'}
              </label>
              <input 
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder={currentLang === 'ar' ? "محمد علي" : "John Doe"} 
                className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm focus:outline-none focus:border-slate-500 rounded-sm text-slate-900 dark:text-white"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                {currentLang === 'ar' ? 'البريد الإلكتروني' : currentLang === 'en' ? 'Email Address' : 'Adresse email'}
              </label>
              <input 
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="contact@example.com" 
                className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm focus:outline-none focus:border-slate-500 rounded-sm text-slate-900 dark:text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                {currentLang === 'ar' ? 'رقم الهاتف' : currentLang === 'en' ? 'Phone Number' : 'Numéro de téléphone'}
              </label>
              <input 
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+213 XX XX XX XX" 
                className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm focus:outline-none focus:border-slate-500 rounded-sm text-slate-900 dark:text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                {currentLang === 'ar' ? 'المؤسسة أو الشركة' : currentLang === 'en' ? 'Company / Organization' : 'Entreprise'}
              </label>
              <input 
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder={currentLang === 'ar' ? "اسم شركتكم أو منظمتكم" : currentLang === 'en' ? "Your Company" : "Votre entreprise"} 
                className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm focus:outline-none focus:border-slate-500 rounded-sm text-slate-900 dark:text-white"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                {currentLang === 'ar' ? 'موضوع الطلب' : currentLang === 'en' ? 'Subject' : 'Sujet'}
              </label>
              <select 
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm focus:outline-none focus:border-slate-500 rounded-sm text-slate-900 dark:text-white"
              >
                <option value="Consulting IT">{currentLang === 'ar' ? 'استشارات تكنولوجيا ونظم المعلومات' : currentLang === 'en' ? 'IT & Governance Consulting' : 'Consulting IT'}</option>
                <option value="Audit">{currentLang === 'ar' ? 'تدقيق ومطابقة الأنظمة' : currentLang === 'en' ? 'IT Audit & Compliance' : 'Audit'}</option>
                <option value="ISO Support">{currentLang === 'ar' ? 'مرافقة واعتماد معايير الآيزو (ISO)' : currentLang === 'en' ? 'ISO Support & Certification' : 'Support ISO'}</option>
                <option value="Formation">{currentLang === 'ar' ? 'برامج التدريب والتأهيل' : currentLang === 'en' ? 'Professional Training' : 'Formation'}</option>
                <option value="Other">{currentLang === 'ar' ? 'موضوع آخر' : currentLang === 'en' ? 'Other' : 'Autre'}</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                {currentLang === 'ar' ? 'تفاصيل الرسالة أو المشروع' : currentLang === 'en' ? 'Message' : 'Message'}
              </label>
              <textarea 
                rows={4}
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                placeholder={currentLang === 'ar' ? "كيف يمكننا مساعدتكم في إنجاز أهدافكم؟" : currentLang === 'en' ? "How can we assist you?" : "Votre message..."} 
                className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm focus:outline-none focus:border-slate-500 rounded-sm text-slate-900 dark:text-white"
              />
            </div>

            <div className="space-y-2 bg-slate-50 dark:bg-slate-800 p-4 rounded-sm border border-slate-200 dark:border-slate-700">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                {currentLang === 'ar' ? 'التحقق الأمني ضد الروبوت :' : currentLang === 'en' ? 'Anti-robot check:' : 'Vérification anti-robot :'} 
                <span className="text-sm font-bold bg-white dark:bg-slate-900 px-2.5 py-1 rounded border border-slate-200 dark:border-slate-600 font-mono">
                  {captchaVal1} + {captchaVal2} = ?
                </span>
              </label>
              <input 
                type="number"
                name="captcha"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                required
                placeholder={currentLang === 'ar' ? "أدخل حاصل الجمع" : currentLang === 'en' ? "Enter the result" : "Saisissez le résultat"} 
                className="w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-3 text-sm focus:outline-none focus:border-slate-500 rounded-sm text-slate-900 dark:text-white"
              />
            </div>
            
            <button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 p-4 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-50 transition-colors rounded-sm mt-4"
            >
              {isSubmitting 
                ? (currentLang === 'ar' ? 'جاري الإرسال...' : currentLang === 'en' ? 'Sending...' : 'Envoi en cours...') 
                : (currentLang === 'ar' ? 'إرسال الرسالة' : currentLang === 'en' ? 'Send Message' : 'Envoyer le message')
              } <Send className="w-4 h-4 rtl:rotate-180" />
            </button>
          </form>
        </div>

      </div>
    </section>
  );
};
